/**
 * The week strip's drawings — moved here from `ClientPreviewPanel.module.css` on 1 September 2026,
 * when the client detail's FREKWENCJA card became the second surface drawing a training week.
 *
 * The declarations are the roster panel's and they are DELETED there rather than copied: two
 * stylesheets carrying one drawing is how the two surfaces learn to disagree, and in a production
 * build the chunk order decides which copy wins.
 */

.strip {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  min-width: 0;
}

/* The marks and the captions under them, as one block. The gap between the two is the gap between
 * two rows of marks, so the captions read as belonging to the column above rather than as a line
 * of their own. */
.calendar {
  display: flex;
  flex-direction: column;
  gap: var(--space-5px);
  min-width: 0;
}

/* ONE row, however long the window is. Owner, 1 September 2026: "zrób te ostatnie 14 dni jako pasek
 * koło siebie, a nie jeden pod drugim." The column count is the caller's `columns` prop, set inline
 * — nothing here names a number of days, and a short read leaves the tail of the track empty rather
 * than stretching three marks across the card.
 *
 * The captions ride on the SAME track, so a date sits under the column it belongs to. */
.grid,
.captions {
  display: grid;
  gap: var(--space-5px);
}

/* One mark per day, and every COLOUR here is the canon's own value.
 *
 * Measured off the KANON roster panel in Flame Dark on 1 September 2026 and mapped onto the tokens
 * that already carry those exact values — no literal, and no "close enough":
 *
 *   odbyta       rgb(255,255,255)          = `--text`      · filled, no edge
 *   pominięta    rgb(255,69,58)            = `--danger`    · see the one deviation below
 *   zaplanowana  rgba(255,255,255,.20)     = `--border-2`  · 1px solid
 *   wolne        rgba(255,255,255,.09)     = `--border`    · 1px dashed
 *
 * `zaplanowana` and `wolne` were drawn in `--border-3` for a measured-contrast reason until this
 * round. **That substitution is WITHDRAWN**: the canon's values are the canon's values. What it
 * costs is recorded rather than hidden — composited against `--bg-card`, `--border-2` measures
 * 1.89:1 in Flame Dark and 1.35:1 in Flame, and `--border` measures 1.28:1 and 1.16:1. Both are
 * under WCAG 1.4.11's 3:1 for a graphic that carries meaning, and `--border-3` cleared it at
 * 3.32–3.60:1. The owner has the numbers; restoring the substitution needs his word, not an
 * agent's judgement. */
.cell {
  display: block;
  height: 26px;
  border-radius: var(--radius-5px);
}

.cellDone {
  background: var(--text);
}

/* **GREY since 16 September 2026, and it was asked for directly.** Owner: *"Pominiete sesje powinny
 * miec by na tym side panelu wyszarzone tez a nie tak krzyczec imo"*, inside a wider instruction to
 * cut the red on the clients screen back to unambiguously bad signals.
 *
 * A missed session is not one. It is a fact about a day that has passed, drawn seven or fourteen at
 * a time, and in `--danger` a client with three quiet days wore three alarms — while the thing that
 * IS a signal, the risk sentence under their name, wore the same colour and lost its voice. The
 * mark keeps its shape: an outline with no fill, which is what tells it from a completed day.
 *
 * `--text-3` rather than `.cellPlanned`'s `--border-2`, so the three outlines stay three: a missed
 * day is the firmest line, a planned one the faintest solid, and a free day dashed. Shape and
 * weight carry it now, with no colour at all — which also settles the note this comment used to
 * carry about the red-versus-grey contrast flipping direction between the two themes.
 *
 * The strip is SHARED: the client detail's FREKWENCJA card draws the same marks, and it changes
 * with this one. That is deliberate — the owner's standing rule is that the two surfaces say it the
 * same way. */
.cellMissed {
  border: var(--border-hairline) solid var(--text-3);
}

.cellPlanned {
  border: var(--border-hairline) solid var(--border-2);
}

.cellFree {
  border: var(--border-hairline) dashed var(--border);
}

/* The caption row. It is a row of its own rather than a label inside each mark, so a cell that
 * carries a caption is exactly as tall as one that does not.
 *
 * `overflow: visible` is the point of the date mode: a date is about two columns wide at a
 * fortnight's column width, and it is allowed to spill across the empty columns beside it. Its
 * neighbours are empty by construction — the component only anchors the two ends and the Mondays
 * far enough from them not to collide. */
.captions {
  overflow: visible;
}

.caption {
  min-width: 0;
  font-size: var(--fs-micro-label);
  font-weight: var(--fw-bold);
  line-height: var(--lh-auto);
  letter-spacing: var(--ls-cat);
  text-align: center;
  white-space: nowrap;
  color: var(--text-3);
}

/* A date spills out of its column, so it is anchored to one edge of it rather than centred on it:
 * left at the start of the strip, and right at the end, where a centred label would hang off the
 * card. */
.captionDate {
  text-align: left;
}

.captionEnd {
  text-align: right;
}

.legend {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-6px) var(--space-14px);
  margin: var(--space-0);
  padding: var(--space-0);
  list-style: none;
}

.legendItem {
  display: inline-flex;
  gap: var(--space-6px);
  align-items: center;
  font-size: var(--fs-eyebrow);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-auto);
  white-space: nowrap;
  color: var(--text-2);
}

/* The swatch is the mark, at the canon's 10×12 — same classes, same edges, so the legend cannot
 * drift away from what the grid draws. */
.legendSwatch {
  display: block;
  flex: none;
  width: 10px;
  height: 12px;
  border-radius: var(--radius-bar);
}
